
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Site } from "@/services/types";
import SiteCardContact from "./SiteCardContact";
import SiteCardHours from "./SiteCardHours";
import SiteCardServices from "./SiteCardServices";
import SiteCardActions from "./SiteCardActions";
import { useSiteUtils } from "./useSiteUtils";
import { Info } from "lucide-react";
import GoogleRating from "./GoogleRating";

interface SiteInfoCardProps {
  site: Site;
  formatOpeningHours: (site: Site) => string;
  getDirectionsUrl: (site: Site) => string;
  isMiniSite?: boolean;
}

const SiteInfoCard = ({ site, formatOpeningHours, getDirectionsUrl, isMiniSite = false }: SiteInfoCardProps) => {
  const { getSitePath } = useSiteUtils();
  const sitePath = getSitePath(site);
  const directionsUrl = getDirectionsUrl(site);

  return (
    <Card className="bg-white shadow-xl border-gray-100 overflow-hidden">
      {/* Header avec dégradé */}
      <div className="bg-gradient-to-r from-fabre-teal to-fabre-green p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
        <div className="relative z-10 flex items-center space-x-3">
          <div className="bg-white/20 rounded-full p-2">
            <Info className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-bold text-white">
              Informations pratiques
            </CardTitle>
            <p className="text-white/90 text-sm">
              Tout ce que vous devez savoir
            </p>
          </div>
        </div>
      </div>

      <CardContent className="p-6 space-y-6">
        {/* Contact */}
        <div className="bg-gradient-to-r from-fabre-lightgreen/20 to-fabre-lightgray/20 rounded-xl p-4">
          <SiteCardContact site={site} isMiniSite={isMiniSite} />
        </div>

        {/* Avis Google */}
        <div className="bg-gradient-to-r from-yellow-50/80 to-orange-50/80 rounded-xl p-4">
          <GoogleRating 
            siteName={site.name}
            address={site.address}
            city={site.city}
          />
        </div>
        
        {!isMiniSite && (
          <>
            {/* Horaires */}
            <div className="bg-gradient-to-r from-fabre-beige/20 to-fabre-lightgreen/20 rounded-xl p-4">
              <SiteCardHours 
                site={site} 
                formatOpeningHours={formatOpeningHours} 
              />
            </div>
            
            {/* Services */}
            <div className="bg-gradient-to-r from-fabre-lightgray/20 to-fabre-beige/20 rounded-xl p-4">
              <SiteCardServices site={site} />
            </div>
          </>
        )}
        
        {/* Actions */}
        <div className="pt-4 border-t border-gray-100">
          <SiteCardActions 
            site={site} 
            sitePath={sitePath} 
            directionsUrl={directionsUrl}
            isMiniSite={isMiniSite}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteInfoCard;
